import React from 'react';
import { useNavigate } from 'react-router-dom';
import { siteConfig } from '../../../config/site';

type Msg = { role: 'user' | 'assistant'; content: string };
type Contato = { nome: string; telefone: string; email: string; localidade: string; tipoAvaria: string; descricao: string };

const FloatingAI: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [showBubble, setShowBubble] = React.useState(false);

  // Define avatar baseado no site
  const getAvatarForSite = () => {
    const domain = siteConfig.domain;
    if (domain === 'alphaclean.pt') {
      return '/images/alpha-clean-logo.webp';
    } else if (domain === 'alphadesentupimentos.pt') {
      return '/images/alpha-desentupimentos-logo.webp';
    } else if (domain === 'alphaeletrica.pt' || domain === 'alphaeletric.pt') {
      return '/images/alpha-eletrica-logo.webp';
    } else if (domain === 'alphaclimatizacao.pt') {
      return '/images/alpha-climatizacao-logo.webp';
    } else if (domain === 'alphaservices.pt') {
      return '/images/alpha services.webp';
    } else {
      // alphacanalizacoes.pt e alphacanalizacoesporto.pt
      return '/images/alpha-canalizacoes-logo.webp';
    }
  };

  const [avatarSrc, setAvatarSrc] = React.useState(getAvatarForSite());
  const [messages, setMessages] = React.useState<Msg[]>([
    { role: 'assistant', content: `Olá! Sou o assistente da ${siteConfig.name}. Como podemos ajudar?` }
  ]);
  const [input, setInput] = React.useState('');
  const [contato, setContato] = React.useState<Contato>({ nome: '', telefone: '', email: '', localidade: '', tipoAvaria: '', descricao: '' });
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState('');

  const hasAll = (c: Contato) => c.nome && c.telefone && c.email && c.tipoAvaria; // localidade é opcional
  const autoDone = React.useRef(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);

  const extractContato = async (msgs: Msg[]) => {
    try {
      const r = await fetch('/api/ai-extract', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: msgs })
      });
      const j = await r.json();
      const e = (j?.extracted || {}) as Partial<Contato>;
      setContato(prev => ({
        ...prev,
        ...(['nome', 'telefone', 'email', 'localidade', 'tipoAvaria', 'descricao'] as const)
          .reduce((acc, k) => ({ ...acc, [k]: typeof (e as any)[k] === 'string' ? (e as any)[k].trim() : (prev as any)[k] }), {} as Contato)
      }));
    } catch { }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const next = [...messages, { role: 'user', content: input.trim() } as Msg];
    setMessages(next);
    setInput('');
    setLoading(true);
    setError('');

    await extractContato(next);

    try {
      const resp = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next, currentContato: contato })
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData?.error || `Erro ${resp.status}: ${resp.statusText}`);
      }

      const data = await resp.json();
      const reply = data?.reply || data?.error || 'Certo! Pode fornecer mais detalhes, por favor?';

      if (data?.error) {
        throw new Error(data.error);
      }

      const next2 = [...next, { role: 'assistant', content: reply } as Msg];
      setMessages(next2);

      // Extrai dados e aguarda atualização
      await extractContato(next2);

      // Se a resposta indica que a conversa foi finalizada, tenta enviar o email
      if (reply.includes('Obrigado') || reply.includes('entrará em contacto') || reply.includes('orçamento')) {
        // Aguarda um pouco para o estado ser atualizado após extractContato
        setTimeout(async () => {
          // Verifica se temos todos os dados necessários
          if (!autoDone.current) {
            // Usa uma verificação final dos dados extraídos
            setContato(currentContato => {
              if (currentContato.nome && currentContato.telefone && currentContato.email && currentContato.tipoAvaria) {
                autoDone.current = true;
                // Chama handleFinish que usará o estado atualizado
                handleFinish();
              }
              return currentContato;
            });
          }
        }, 500);
      }
    } catch (err: any) {
      console.error('Erro ao enviar mensagem:', err);
      const errorMsg = err?.message || 'Desculpe, não consegui responder agora. Verifique se o servidor está rodando e tente novamente.';
      setMessages(m => [...m, { role: 'assistant', content: errorMsg }]);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishWithData = async (contatoData: Contato, messagesData: Msg[]) => {
    try {
      setLoading(true);
      setError('');

      // Valida dados mínimos antes de enviar
      if (!contatoData.nome || !contatoData.telefone || !contatoData.email || !contatoData.tipoAvaria) {
        throw new Error('Dados incompletos. Por favor, preencha nome, telefone, email e tipo de serviço.');
      }

      // Tenta gerar resumo da conversa (opcional - não bloqueia o envio)
      let summary = '';
      try {
        const r = await fetch('/api/ai-summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: messagesData, contato: contatoData })
        });

        if (r.ok) {
          const s = await r.json();
          summary = s?.summary || '';
        } else {
          console.warn('Não foi possível gerar resumo, mas continuando com envio de email...');
        }
      } catch (summaryError) {
        console.warn('Erro ao gerar resumo (continuando mesmo assim):', summaryError);
        // Continua mesmo se o resumo falhar
      }

      let mail;
      const isEletricaSite = siteConfig.domain === 'alphaeletrica.pt' || siteConfig.domain === 'alphaeletric.pt';

      if (siteConfig.domain === 'alphaclean.pt') {
        // alphaclean.pt: mesma regra do QuoteForm, entrega directa por FormSubmit.
        mail = await fetch('https://formsubmit.co/ajax/alphaservices.pt@gmail.com', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            _subject: `NOVO ORÇAMENTO ASSISTENTE [${siteConfig.name}]: ${contatoData.tipoAvaria}`,
            _template: 'table',
            Nome: contatoData.nome,
            Telefone: contatoData.telefone,
            Email: contatoData.email,
            Localidade: contatoData.localidade || 'N/A',
            Servico: contatoData.tipoAvaria,
            Descricao: contatoData.descricao || 'N/A',
            Conversa_Resumo: summary || 'Nenhum resumo gerado'
          })
        });
      } else if (isEletricaSite) {
        mail = await fetch('https://formsubmit.co/ajax/Alphaeletricapt@gmail.com', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            _subject: `NOVO ORÇAMENTO ASSISTENTE [${siteConfig.name}]: ${contatoData.tipoAvaria}`,
            _template: 'table',
            Nome: contatoData.nome,
            Telefone: contatoData.telefone,
            Email: contatoData.email,
            Localidade: contatoData.localidade || 'N/A',
            Servico: contatoData.tipoAvaria,
            Descricao: contatoData.descricao || 'N/A',
            Conversa_Resumo: summary || 'Nenhum resumo gerado'
          })
        });
      } else {
        // Envia para Web3Forms nos outros sites
        mail = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            access_key: siteConfig.web3formsKey || 'da198dbc-68bf-4341-b543-3c734419e8f0',
            subject: `NOVO ORÇAMENTO ASSISTENTE [${siteConfig.name}]: ${contatoData.tipoAvaria}`,
            from_name: `${siteConfig.name} (Assistente)`,
            Nome: contatoData.nome,
            Telefone: contatoData.telefone,
            Email: contatoData.email,
            Localidade: contatoData.localidade || 'N/A',
            Servico: contatoData.tipoAvaria,
            Descricao: contatoData.descricao || 'N/A',
            Conversa_Resumo: summary || 'Nenhum resumo gerado'
          })
        });
      }

      if (!mail.ok) {
        const errorData = await mail.json().catch(() => ({}));
        const errorMessage = errorData?.error || `Erro ao enviar email: ${mail.status}`;
        console.error('Erro ao enviar email:', errorMessage);
        throw new Error(errorMessage);
      }

      // Sucesso!
      const mailResult = await mail.json().catch(() => ({}));
      console.log('Email enviado com sucesso:', mailResult);

      setSent(true);
      // Redireciona para página de obrigado após 2 segundos
      setTimeout(() => {
        navigate('/obrigado');
      }, 2000);
    } catch (err: any) {
      console.error('Erro ao finalizar pedido:', err);
      const errorMessage = err?.message || 'Falha ao enviar. Tente novamente.';
      setError(errorMessage);
      setMessages(m => [...m, {
        role: 'assistant',
        content: `Desculpe, houve um erro ao enviar o pedido: ${errorMessage}. Por favor, tente novamente ou contacte-nos diretamente pelo telefone ${siteConfig.phoneDisplay}.`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    await handleFinishWithData(contato, messages);
  };

  // Quando completar todos os dados obrigatórios, finaliza automaticamente uma vez
  React.useEffect(() => {
    if (open && !sent && !loading && hasAll(contato) && !autoDone.current) {
      autoDone.current = true;
      handleFinish();
    }
  }, [contato, open, sent, loading]);

  // Reset autoDone quando fecha o chat
  React.useEffect(() => {
    if (!open) {
      autoDone.current = false;
    }
  }, [open]);

  // Scroll automático para o final quando novas mensagens são adicionadas
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, loading]);

  // Scroll automático quando o chat é aberto
  React.useEffect(() => {
    if (open && messagesEndRef.current) {
      // Pequeno delay para garantir que o DOM está renderizado
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      }, 100);
    }
  }, [open]);

  // Verifica se o balão já foi mostrado antes (localStorage)
  const bubbleShownKey = 'chat-bubble-shown';
  const [hasShownBubble, setHasShownBubble] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(bubbleShownKey) === 'true';
    }
    return false;
  });

  // Verifica se o usuário rolou até a seção de serviços
  const [servicesVisible, setServicesVisible] = React.useState(false);
  const bubbleTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const checkServicesVisibility = () => {
      const servicesSection = document.getElementById('servicos');
      if (servicesSection) {
        const rect = servicesSection.getBoundingClientRect();
        // Se a seção está visível na viewport (mesmo que parcialmente)
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible) {
          setServicesVisible(true);
        }
      }
    };

    // Verifica imediatamente e ao fazer scroll
    checkServicesVisibility();
    window.addEventListener('scroll', checkServicesVisibility, { passive: true });
    window.addEventListener('resize', checkServicesVisibility, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkServicesVisibility);
      window.removeEventListener('resize', checkServicesVisibility);
    };
  }, []);

  // Abre o balão automaticamente após 2 segundos quando a seção de serviços estiver visível
  React.useEffect(() => {
    // Limpa timer anterior se houver
    if (bubbleTimerRef.current) {
      clearTimeout(bubbleTimerRef.current);
      bubbleTimerRef.current = null;
    }

    // Só mostra se a seção está visível, o balão não está aberto, e ainda não foi mostrado antes
    // Na climatização o balão abria por cima dos botões de chamada/orçamento no telemóvel
    if (siteConfig.domain === 'alphaclimatizacao.pt') {
      return;
    }
    if (!servicesVisible || showBubble || hasShownBubble || open) {
      return;
    }

    bubbleTimerRef.current = setTimeout(() => {
      // Verifica novamente antes de mostrar
      if (!hasShownBubble && !showBubble && !open) {
        setShowBubble(true);
      }
      bubbleTimerRef.current = null;
    }, 2000);

    return () => {
      if (bubbleTimerRef.current) {
        clearTimeout(bubbleTimerRef.current);
        bubbleTimerRef.current = null;
      }
    };
  }, [servicesVisible, showBubble, hasShownBubble, open]);

  // Função para fechar o balão e marcar como mostrado
  const closeBubble = React.useCallback(() => {
    setShowBubble(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem(bubbleShownKey, 'true');
    }
    setHasShownBubble(true);
  }, []);

  // Se o usuário abrir o chat, esconde o balão e marca como mostrado
  React.useEffect(() => {
    if (open && showBubble) {
      setShowBubble(false);
      // Marca como mostrado quando abre o chat
      if (typeof window !== 'undefined') {
        localStorage.setItem(bubbleShownKey, 'true');
      }
      setHasShownBubble(true);
    }
  }, [open]);

  return (
    <>
      {/* Balão de chat acima do botão */}
      {showBubble && !open && (
        <div
          className="fixed z-[65] right-5 bottom-40 w-[252px] bg-white rounded-2xl shadow-xl border border-gray-200 p-4"
          style={{
            animation: 'slideUpFade 0.3s ease-out',
            transformOrigin: 'bottom right'
          }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {avatarSrc ? (
                <img src={avatarSrc} alt="Atendente" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-user-line text-indigo-600 text-xl"></i>
              )}
            </div>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setOpen(true)}>
              <div className="bg-gray-100 text-gray-800 rounded-lg px-3 py-2 text-sm">
                {messages[0]?.content || "Olá! Como posso ajudar?"}
              </div>
            </div>
            <button
              onClick={closeBubble}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              aria-label="Fechar balão"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          {/* Seta do balão */}
          <div className="absolute bottom-0 right-8 transform translate-y-1/2 rotate-45 w-3 h-3 bg-white border-r border-b border-gray-200"></div>
        </div>
      )}

      {/* Botão redondo com rosto */}
      <button
        aria-label="Peça seu orçamento"
        onClick={() => setOpen(true)}
        className="fixed z-[60] right-5 bottom-28 bg-indigo-600 hover:bg-indigo-700 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors overflow-hidden"
      >
        {avatarSrc ? (
          <img src={avatarSrc} alt="Atendente" className="w-full h-full object-cover" />
        ) : (
          <i className="ri-user-line text-2xl"></i>
        )}
      </button>

      {open && (
        <div className="fixed z-[70] right-5 bottom-5 w-[360px] max-w-[90vw] h-[580px] max-h-[calc(100vh-2.5rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col">
          <div className="h-12 px-4 flex items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Atendente" className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-line text-indigo-600"></i>
                )}
              </div>
              <span className="font-semibold text-gray-900">Atendente</span>
            </div>
            <button
              aria-label="Fechar chat"
              onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 hover:bg-red-50 rounded-full transition-colors border border-gray-300 hover:border-red-300 flex-shrink-0"
              title="Fechar chat"
            >
              <i className="ri-close-line text-lg"></i>
              <span className="sr-only">Fechar</span>
            </button>
          </div>

          {!sent ? (
            <>
              <div ref={messagesContainerRef} className="flex-1 overflow-auto p-4 space-y-3">
                {messages.map((m, i) => (
                  <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                    <div className={`inline-block px-3 py-2 rounded-lg text-sm ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="text-left">
                    <div className="inline-block px-3 py-2 rounded-lg text-sm bg-gray-100 text-gray-800">
                      <span className="inline-flex items-center gap-2">
                        <span className="animate-pulse">●</span>
                        A pensar...
                      </span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t space-y-3">
                <div className="flex gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendMessage(); } }}
                    placeholder="Escreva a sua mensagem..."
                    className="flex-1 border rounded-lg px-3 py-2"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 rounded-lg"
                  >
                    {loading ? 'A enviar...' : 'Enviar'}
                  </button>
                </div>
                {error && <div className="text-red-600 text-sm">{error}</div>}
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-center p-6">
              <div>
                <div className="text-2xl mb-2">✅</div>
                <div className="font-semibold">Pedido enviado!</div>
                <div className="text-gray-600 text-sm mt-1">A nossa equipa entrará em contacto em breve com o orçamento.</div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FloatingAI;


